import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import {
  Star, MessageCircle, Camera, Hash, FileText, MapPin, Phone, Globe,
  Image as ImageIcon, CheckCircle2, Tag, Clock, X, Search, BadgeCheck, Users,
} from 'lucide-react';
import MarketingPageHeader from '../components/marketing/MarketingPageHeader';
import { useMarketingEmbedded } from '../hooks/useMarketingEmbedded';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from '../hooks/useCompanyId';
import { getAccountById } from '../services/firestoreService';
import { getReviewsData } from '../services/reviewsService';
import PortalBackground from '../components/PortalBackground';

// Shared card style (ClientDashboard design language)
const CARD = 'rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm';

// The six pieces of a complete Google Business Profile
const PROFILE_CHECKS = [
  { key: 'claimed', label: 'Profile claimed', icon: BadgeCheck, tip: 'Claiming your profile lets you respond to reviews and edit your listing.' },
  { key: 'address', label: 'Address', icon: MapPin, tip: 'A listed address helps you show up in local map results.' },
  { key: 'phone', label: 'Phone number', icon: Phone, tip: 'Makes it easy for customers to call you straight from Google.' },
  { key: 'website', label: 'Website link', icon: Globe, tip: 'Sends profile visitors directly to your website.' },
  { key: 'hours', label: 'Business hours', icon: Clock, tip: 'Customers check your hours before reaching out.' },
  { key: 'logo', label: 'Logo uploaded', icon: ImageIcon, tip: 'A logo makes your profile look polished and established.' },
];

// overrideCompanyId/overrideUserId let an internal viewer (CRM Portal View)
// load a specific client account's reviews audit instead of their own.
const ReviewsDashboard = ({ embedded: embeddedProp, overrideCompanyId = null, overrideUserId = null }) => {
  const embedded = useMarketingEmbedded(embeddedProp);
  const { currentUser } = useAuth();
  const { companyId: resolvedCompanyId } = useCompanyId();
  const companyId = overrideCompanyId || resolvedCompanyId;
  // The uid whose reviews data is read.
  const effectiveUid = overrideUserId || currentUser?.uid;

  // New audit modal state
  const [showNewAuditModal, setShowNewAuditModal] = useState(false);
  const [newAuditName, setNewAuditName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('success');
  const [companyName, setCompanyName] = useState('');

  // Reviews data
  const [data, setData] = useState({
    categoryIds: [],
    additionalIds: [],
    totalPhotos: 0,
    totalReviews: 0,
    newThisMonth: 0,
    averageRating: 0,
    ratingDistribution: [],
    fiveStarReviews: 0,
    competitors: [],
    businessMetrics: {
      totalTopics: 0,
      topicReviews: 0,
      address: false,
      phone: false,
      website: false,
      logo: false,
      hours: false,
      claimed: false,
      attributes: 0,
    },
  });
  const [hasData, setHasData] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  // Load reviews data on component mount
  useEffect(() => {
    const loadReviewsData = async () => {
      if (effectiveUid) {
        try {
          setDataLoading(true);
          const result = await getReviewsData(effectiveUid);

          if (result.success && result.data) {
            console.log('📊 Loaded reviews data:', result.data);
            setData((prev) => ({ ...prev, ...result.data }));
            setHasData(true);
          } else {
            console.log('📭 No reviews data found');
          }
        } catch (error) {
          console.error('❌ Error loading reviews data:', error);
          showNotificationMessage('❌ Failed to load reviews data', 'error');
        } finally {
          setDataLoading(false);
        }
      } else {
        setDataLoading(false);
      }
    };

    loadReviewsData();
  }, [currentUser, effectiveUid]);

  // Load company name from the shared account profile for audit prefill
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    getAccountById(companyId)
      .then((account) => {
        if (!cancelled && account?.company) setCompanyName(account.company);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [companyId]);

  // Show notification
  const showNotificationMessage = (message, type = 'success') => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 5000);
  };

  // Open the audit modal with the profile name prefilled from the company profile
  const openNewAuditModal = () => {
    if (!newAuditName.trim() && companyName) {
      setNewAuditName(companyName);
    }
    setShowNewAuditModal(true);
  };

  // Run new audit function
  const runNewAudit = async () => {
    console.log('🔍 Starting Google Business Profile audit...');

    if (newAuditName.trim() && currentUser?.uid) {
      try {
        setIsLoading(true);

        // Prepare data for Zapier webhook
        const webhookData = {
          accountId: effectiveUid, // Using user ID as account ID
          userId: effectiveUid,
          googleMyBusinessProfileName: newAuditName.trim(),
        };

        // Send data to Zapier webhook
        const response = await fetch('https://hooks.zapier.com/hooks/catch/17294713/uu03zgi/', {
          method: 'POST',
          body: JSON.stringify(webhookData),
        });

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Webhook response:', result);

          // Close modal and reset form
          setShowNewAuditModal(false);
          setNewAuditName('');
          setIsLoading(false);

          showNotificationMessage(`🚀 Google Business Profile audit started for "${newAuditName}"! Results will be available shortly.`, 'success');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.error('❌ Error sending audit request:', error);
        showNotificationMessage(`❌ Audit request failed: ${error.message}`, 'error');
        setIsLoading(false);
      }
    } else {
      if (!currentUser?.uid) {
        showNotificationMessage('❌ Please log in to run an audit.', 'error');
      } else {
        showNotificationMessage('❌ Please enter a valid profile name.', 'error');
      }
    }
  };

  // ---- Derived helpers ----
  const bm = data.businessMetrics || {};
  const completedChecks = PROFILE_CHECKS.filter((c) => bm[c.key]).length;
  const completeness = Math.round((completedChecks / PROFILE_CHECKS.length) * 100);
  const rating = Number(data.averageRating) || 0;

  // Plain-English prioritized action items based on the audit
  const buildPriorities = () => {
    if (!hasData) return [];
    const candidates = [];
    if (!bm.claimed) {
      candidates.push({ weight: 100, text: "Claim your Google Business Profile — until you do, you can't respond to reviews or update your listing." });
    }
    const missing = PROFILE_CHECKS.filter((c) => c.key !== 'claimed' && !bm[c.key]).map((c) => c.label.toLowerCase());
    if (missing.length > 0) {
      candidates.push({ weight: 90, text: `Complete your profile — add your ${missing.join(', ')}. Complete profiles rank higher in local search.` });
    }
    if ((data.totalReviews || 0) < 20) {
      candidates.push({ weight: 80, text: 'Grow your review count — ask every happy customer for a Google review. 20+ reviews builds instant trust.' });
    }
    if (rating > 0 && rating < 4.5) {
      candidates.push({ weight: 75, text: `Raise your ${rating} rating — respond professionally to negative reviews and follow up with your best customers.` });
    }
    if ((data.totalPhotos || 0) < 10) {
      candidates.push({ weight: 60, text: 'Add photos of your best projects — profiles with photos get far more clicks and calls.' });
    }
    let actions = candidates.sort((a, b) => b.weight - a.weight).slice(0, 3).map((c) => c.text);
    if (actions.length === 0) {
      actions = ['Keep collecting reviews and posting fresh photos to stay ahead of competitors.'];
    }
    return actions;
  };

  const heroHeadline = !hasData
    ? 'Run your first audit to see how your Google presence stacks up.'
    : rating >= 4.5 && completeness >= 80
      ? 'Your Google presence is strong — keep it up!'
      : rating >= 4
        ? 'Good foundation — a few tweaks will make you stand out.'
        : 'Your Google profile needs attention to win local customers.';

  // Star row for the hero rating
  const StarRow = ({ value, size = 'h-6 w-6' }) => (
    <div className="flex items-center justify-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${size} ${i <= Math.round(value) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
        />
      ))}
    </div>
  );

  // Stat tiles shared by both views
  const statTiles = [
    { icon: MessageCircle, tile: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400', value: (data.totalReviews || 0).toLocaleString(), label: 'Total Reviews', sub: 'across your profile' },
    { icon: Star, tile: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400', value: (data.fiveStarReviews || 0).toLocaleString(), label: '5-Star Reviews', sub: data.totalReviews > 0 ? `${Math.round(((data.fiveStarReviews || 0) / data.totalReviews) * 100)}% of all reviews` : 'no reviews yet' },
    { icon: Camera, tile: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400', value: (data.totalPhotos || 0).toLocaleString(), label: 'Photos', sub: 'on your profile' },
    { icon: BadgeCheck, tile: completeness >= 80 ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400', value: `${completeness}%`, label: 'Profile Complete', sub: `${completedChecks} of ${PROFILE_CHECKS.length} items done` },
  ];

  const StatTiles = () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {statTiles.map((tile) => {
        const Icon = tile.icon;
        return (
          <div key={tile.label} className={`${CARD} p-5 text-center`}>
            <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg mb-2 ${tile.tile}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="text-2xl font-bold text-gray-800 dark:text-white">{tile.value}</div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{tile.label}</div>
            <div className="text-xs mt-0.5 text-gray-500 dark:text-gray-500">{tile.sub}</div>
          </div>
        );
      })}
    </div>
  );

  // Profile completeness checklist card
  const CompletenessCard = ({ showTips = false }) => (
    <div className={`${CARD} p-6`}>
      <h2 className="text-lg font-bold text-gray-800 dark:text-white text-center mb-1">Profile Completeness</h2>
      <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-4">
        Complete profiles show up more often in local search
      </p>
      <div className="w-full rounded-full h-2 bg-gray-200 dark:bg-gray-700 mb-5">
        <div
          className={`h-2 rounded-full ${completeness >= 80 ? 'bg-green-500' : completeness >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
          style={{ width: `${completeness}%` }}
        ></div>
      </div>
      <div className={`grid grid-cols-1 ${showTips ? '' : 'sm:grid-cols-2'} gap-2`}>
        {PROFILE_CHECKS.map((check) => {
          const done = Boolean(bm[check.key]);
          const Icon = check.icon;
          return (
            <div key={check.key} className={`flex items-start gap-3 p-3 rounded-lg ${done ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
              {done ? (
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              ) : (
                <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800 dark:text-white">
                  <Icon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                  {check.label}
                </div>
                {showTips && !done && (
                  <p className="text-xs mt-0.5 text-gray-500 dark:text-gray-400">{check.tip}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Competitors card
  const CompetitorsCard = () => (
    (data.competitors || []).length > 0 && (
      <div className={`${CARD} p-6`}>
        <h2 className="text-lg font-bold text-gray-800 dark:text-white text-center mb-1 flex items-center justify-center gap-2">
          <Users className="w-5 h-5 text-indigo-500" />
          Competitors Customers Also Searched
        </h2>
        <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-4">
          Businesses that show up alongside yours on Google
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {data.competitors.map((competitor, index) => (
            <div key={index} className="flex lg:flex-col items-center gap-3 lg:gap-2 p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 lg:text-center">
              <div className="w-7 h-7 flex-shrink-0 bg-gray-600 dark:bg-gray-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                {index + 1}
              </div>
              <span className="text-sm font-medium text-gray-800 dark:text-white">{competitor.name}</span>
            </div>
          ))}
        </div>
      </div>
    )
  );

  // New Audit Modal — rendered as a plain function (not a nested component) so
  // the input keeps focus while typing triggers parent re-renders
  const renderNewAuditModal = (onClose) => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="max-w-md w-full rounded-xl shadow-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
        <div className="relative mb-6">
          <div className="flex flex-col items-center text-center">
            <div className="w-10 h-10 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mb-2">
              <Star className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-white">Run Google Profile Audit</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={`absolute top-0 right-0 text-2xl leading-none text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            ×
          </button>
        </div>

        {!isLoading ? (
          <>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Google Business Profile Name
              </label>
              <input
                type="text"
                placeholder="Your Business Name"
                value={newAuditName}
                onChange={(e) => setNewAuditName(e.target.value)}
                className="w-full p-3 border rounded-lg focus:ring-red-500 focus:border-red-500 border-gray-300 bg-white text-gray-900 placeholder-gray-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                {companyName && newAuditName === companyName
                  ? '✓ Prefilled from your company profile — edit if your Google listing uses a different name'
                  : 'Enter your profile name exactly as it appears on Google'}
              </p>
              {!currentUser?.uid && (
                <p className="text-xs mt-2 text-red-500">
                  ⚠️ You must be logged in to run Google Business Profile audits
                </p>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border transition-colors duration-200 font-medium border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={runNewAudit}
                disabled={!newAuditName.trim() || !currentUser?.uid}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
              >
                {!currentUser?.uid ? 'Please Login First' : 'Start Audit'}
              </button>
            </div>
          </>
        ) : (
          <div className="text-center">
            <BBALoader size={48} className="mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-800 dark:text-white">
              Creating Google Business Profile audit...
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
              Please wait while we set up your audit...
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Loading state
  if (dataLoading) {
    return (
      <div className={embedded ? '' : 'relative min-h-screen'}>
        {!embedded && <PortalBackground />}
        <div className={`relative max-w-7xl mx-auto ${embedded ? '' : 'p-4 md:p-8'}`}>
          {!embedded && <MarketingPageHeader title="Google Business Profile" />}
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <BBALoader size={48} className="mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-800 dark:text-white">Loading your Google profile data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const priorities = buildPriorities();

  return (
    <div className={embedded ? '' : 'relative min-h-screen'}>
      {!embedded && <PortalBackground />}
      <div className={`relative max-w-7xl mx-auto ${embedded ? '' : 'p-4 md:p-8'}`}>
        {!embedded && <MarketingPageHeader title="Google Business Profile" />}

        {/* Empty state — no audit yet (no card background) */}
        {!hasData && (
          <div className="py-10 text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-500 mb-4">
              <Star className="w-7 h-7" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{heroHeadline}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xl mx-auto mb-6">
              We'll pull your Google Business Profile: reviews, ratings, photos, profile completeness, and the
              competitors customers see next to you, then show you exactly what to improve.
            </p>
            <button
              onClick={openNewAuditModal}
              className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              <Search className="w-4 h-4" />
              Run Your First Audit
            </button>
          </div>
        )}

        {/* Everything on one page: headline content first, supporting detail below */}
        {hasData && (
          <div className="space-y-6 mb-10">
            {/* Rating hero */}
            <div className={`relative ${CARD} p-8`}>
              <button
                onClick={openNewAuditModal}
                aria-label="Run New Audit"
                title="Run New Audit"
                className="absolute top-4 right-4 inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 text-sm font-medium"
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Run New Audit</span>
              </button>
              <div className="flex flex-col items-center text-center">
                <div className="text-6xl font-extrabold text-gray-800 dark:text-white mb-2">
                  {rating ? rating.toFixed(1) : '—'}
                </div>
                <StarRow value={rating} />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Based on {(data.totalReviews || 0).toLocaleString()} Google review{data.totalReviews === 1 ? '' : 's'}
                  {data.lastUpdated && (
                    <span className="text-gray-400 dark:text-gray-500">
                      {' · '}Last updated: {new Date(data.lastUpdated).toLocaleDateString()}
                    </span>
                  )}
                </p>
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white mt-4">{heroHeadline}</h2>
              </div>
            </div>

            {/* Key numbers */}
            <StatTiles />

            {/* Top priorities */}
            <div className={`${CARD} p-6`}>
              <h3 className="text-xl font-bold mb-4 text-center text-gray-800 dark:text-white">
                {priorities.length === 1 ? 'Your Top Priority' : `Your Top ${priorities.length} Priorities`}
              </h3>
              <div className="space-y-3 max-w-2xl mx-auto">
                {priorities.map((action, i) => (
                  <div key={i} className="flex items-start p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full mr-3 text-sm font-bold bg-red-600 text-white">
                      {i + 1}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-200">{action}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Profile completeness + rating breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CompletenessCard showTips />

              <div className={`${CARD} p-6`}>
                <h2 className="text-lg font-bold text-gray-800 dark:text-white text-center mb-1">Rating Distribution</h2>
                <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-4">
                  How your {(data.totalReviews || 0).toLocaleString()} reviews break down
                </p>
                <div className="max-w-md mx-auto space-y-3">
                  {(data.ratingDistribution || []).map((r) => (
                    <div key={r.stars} className="flex items-center">
                      <div className="w-10 text-gray-700 dark:text-gray-300 flex items-center text-sm">
                        <span>{r.stars}</span>
                        <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400 ml-1" />
                      </div>
                      <div className="flex-1 mx-3">
                        <div className="bg-gray-200 dark:bg-gray-700 h-3 rounded-full overflow-hidden">
                          <div className="bg-yellow-400 h-3" style={{ width: `${r.percentage}%` }}></div>
                        </div>
                      </div>
                      <div className="w-20 text-right text-gray-600 dark:text-gray-400 text-xs">
                        {r.count} ({r.percentage}%)
                      </div>
                    </div>
                  ))}
                  {(data.ratingDistribution || []).length === 0 && (
                    <p className="text-sm text-center text-gray-500 dark:text-gray-400">No reviews yet.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Competitors */}
            <CompetitorsCard />

            {/* Divider: supporting profile details */}
            <div className="pt-2 flex items-center gap-4">
              <div className="flex-1 border-t border-gray-200 dark:border-gray-800" />
              <div className="text-center">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Profile Details
                </h2>
                <p className="text-xs mt-0.5 text-gray-400 dark:text-gray-500">
                  The supporting detail behind your listing
                </p>
              </div>
              <div className="flex-1 border-t border-gray-200 dark:border-gray-800" />
            </div>

            {/* Categories + Services + Engagement */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className={`${CARD} p-6`}>
                <h2 className="text-base font-semibold text-gray-800 dark:text-white text-center mb-1 flex items-center justify-center gap-2">
                  <Tag className="w-4 h-4 text-blue-500" />
                  Business Categories
                </h2>
                <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-4">
                  How Google classifies your business
                </p>
                {(data.categoryIds || []).length > 0 ? (
                  <div className="flex flex-wrap justify-center gap-2">
                    {data.categoryIds.map((category) => (
                      <span key={category.id} className="px-3 py-1.5 rounded-full text-sm font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                        {category.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-center text-gray-500 dark:text-gray-400">No categories found yet.</p>
                )}
              </div>

              <div className={`${CARD} p-6`}>
                <h2 className="text-base font-semibold text-gray-800 dark:text-white text-center mb-1 flex items-center justify-center gap-2">
                  <FileText className="w-4 h-4 text-emerald-500" />
                  Additional Services
                </h2>
                <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-4">
                  Extra services listed on your profile
                </p>
                {(data.additionalIds || []).length > 0 ? (
                  <div className="flex flex-wrap justify-center gap-2">
                    {data.additionalIds.map((additional) => (
                      <span key={additional.id} className="px-3 py-1.5 rounded-full text-sm font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                        {additional.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-center text-gray-500 dark:text-gray-400">No additional services listed yet.</p>
                )}
              </div>

              <div className={`${CARD} p-6`}>
                <h2 className="text-base font-semibold text-gray-800 dark:text-white text-center mb-1 flex items-center justify-center gap-2">
                  <Hash className="w-4 h-4 text-indigo-500" />
                  Engagement
                </h2>
                <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-4">
                  Topics and attributes Google tracks for you
                </p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                    <div className="text-xl font-bold text-gray-800 dark:text-white">{bm.totalTopics || 0}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Topics</div>
                  </div>
                  <div className="p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                    <div className="text-xl font-bold text-gray-800 dark:text-white">{bm.topicReviews || 0}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Topic Reviews</div>
                  </div>
                  <div className="p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                    <div className="text-xl font-bold text-gray-800 dark:text-white">{bm.attributes || 0}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Attributes</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification */}
        {showNotification && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-md ${
            notificationType === 'success'
              ? 'bg-green-500 text-white'
              : 'bg-red-500 text-white'
          }`}>
            <div className="flex items-center">
              <span className="text-lg mr-2">
                {notificationType === 'success' ? '✅' : '❌'}
              </span>
              <p className="text-sm">{notificationMessage}</p>
            </div>
          </div>
        )}

        {/* New Audit Modal */}
        {showNewAuditModal && renderNewAuditModal(() => {
          setShowNewAuditModal(false);
          setNewAuditName('');
        })}
      </div>
    </div>
  );
};

export default ReviewsDashboard;
